import { filterErrorsByField } from 'Automations/automationEditor/utils'
import { Box, Grid } from '@sequencehq/core-components'
import {
  ConditionPropertyEditorComponent,
  CustomPropertyConditionValue,
  CustomPropertySubOperator,
  readCustomPropertyValue
} from 'Automations/nodeDefinitions/conditions'
import {
  InputSelectControlField,
  TextInputField
} from 'Automations/nodeDefinitions/components'
import { CustomerOperatorSelect } from './CustomerOperatorSelect'
import { appendTestId } from './CustomerPropertyEditor.helpers'

const SUB_OPERATOR_OPTIONS: Array<{
  label: string
  value: CustomPropertySubOperator
}> = [
  { label: 'Equals', value: 'EQUALS' },
  { label: 'Does not equal', value: 'NOT_EQUALS' }
]

/**
 * Editor for a custom-property operator (`CUSTOM_PROPERTY:<key>`). Row 2 holds
 * the primary operator dropdown (the custom property) and the equals /
 * does-not-equal sub-operator; row 3 is the full-width free-text value. The
 * stored value is `{ operator, value }` — the key lives in the operator id.
 */
export const CustomPropertyOperatorEditor: ConditionPropertyEditorComponent = ({
  'data-testid': dataTestId,
  operator,
  value,
  onChange,
  operators,
  siblingConditions,
  validationErrors,
  focusOnInitialLoad
}) => {
  const { operator: subOperator, value: comparand } =
    readCustomPropertyValue(value)

  const update = (next: Partial<CustomPropertyConditionValue>) =>
    onChange({
      value: {
        operator: next.operator ?? subOperator ?? 'EQUALS',
        value: next.value ?? comparand
      }
    })

  return (
    <Grid
      gridTemplateColumns="2fr 3fr"
      gap="8px"
      gridTemplateAreas='"operator subOperator" "value value"'
    >
      <Box gridArea="operator">
        <CustomerOperatorSelect
          data-testid={dataTestId}
          operator={operator}
          operators={operators}
          siblingConditions={siblingConditions}
          onChange={onChange}
          validationErrors={validationErrors?.operator}
          initiallyOpen={focusOnInitialLoad}
        />
      </Box>
      <Box gridArea="subOperator">
        <InputSelectControlField
          data-testid={appendTestId(dataTestId, 'subOperator')}
          value={subOperator ?? 'EQUALS'}
          onChange={selected =>
            update({ operator: selected as CustomPropertySubOperator })
          }
          options={SUB_OPERATOR_OPTIONS}
        />
      </Box>
      <Box gridArea="value">
        <TextInputField
          data-testid={appendTestId(dataTestId, 'value')}
          value={comparand ?? ''}
          placeholder="Enter value..."
          onChange={e => update({ value: e.target.value })}
          validationErrors={filterErrorsByField(
            validationErrors?.value,
            'value'
          )}
        />
      </Box>
    </Grid>
  )
}
